<template>
  <div class="app-container seo-cycle-page">
    <el-card shadow="never">
      <div class="page-head">
        <div>
          <div class="page-title">SEO 全站月度自动执行</div>
          <div class="page-subtitle">
            以 Cycle 为最高执行单元，分 Run
            提交、集中采集，并按站点原子发布排名结果
          </div>
        </div>
        <div class="head-actions">
          <el-button
            v-if="canSync"
            size="small"
            :loading="manualCollectLoading"
            @click="manualCollect"
          >
            立即采集一轮
          </el-button>
          <el-button
            size="small"
            type="primary"
            :loading="refreshing"
            @click="refreshAll"
          >
            刷新
          </el-button>
        </div>
      </div>

      <el-alert
        class="semantic-alert"
        type="info"
        :closable="false"
        show-icon
        title="调度检查频率不等于付费查询频率"
        description="后台每5分钟检查一次到期和续跑状态；每个自然月只创建一个全站付费Cycle。提交状态未知时先对账，禁止盲目重复POST。"
      ></el-alert>

      <el-tabs v-model="activeTab" @tab-click="onTabChange">
        <el-tab-pane label="计划预览" name="plan">
          <div class="schedule-panel">
            <div>
              <span>计划周期</span>
              <strong>{{ plan.period_key || "-" }}</strong>
            </div>
            <div>
              <span>查询日期与时间</span>
              <strong>{{ plan.scheduled_text || "-" }}</strong>
            </div>
            <div>
              <span>固定时区</span>
              <strong>{{ plan.timezone || "Asia/Shanghai" }}</strong>
            </div>
            <div>
              <span>当前口径</span>
              <el-tag size="small" :type="planDueTagType">
                {{ planDueText }}
              </el-tag>
            </div>
          </div>

          <div v-loading="planLoading" class="metric-grid">
            <div class="metric-card">
              <span>站点</span>
              <strong>{{ planSummary.site_count || 0 }}</strong>
            </div>
            <div class="metric-card">
              <span>关键词 Task</span>
              <strong>{{ planSummary.task_count || 0 }}</strong>
            </div>
            <div class="metric-card">
              <span>Run</span>
              <strong>{{ planSummary.run_count || 0 }}</strong>
            </div>
            <div class="metric-card">
              <span>Batch</span>
              <strong>{{ planSummary.batch_count || 0 }}</strong>
            </div>
            <div class="metric-card">
              <span>整批</span>
              <strong>{{ planSummary.full_batch_count || 0 }}</strong>
            </div>
            <div class="metric-card">
              <span>尾批 Task</span>
              <strong>{{ planSummary.tail_task_count || 0 }}</strong>
            </div>
            <div class="metric-card">
              <span>单 Run 上限</span>
              <strong>{{ planSummary.run_limit || 0 }}</strong>
            </div>
            <div class="metric-card">
              <span>预计费用</span>
              <strong>{{ formatUsd(planSummary.estimated_cost) }}</strong>
            </div>
          </div>

          <el-alert
            v-if="planSummary.invalid_count || planSummary.capacity_warning"
            class="section-gap"
            type="warning"
            :closable="false"
            show-icon
            :title="planWarningText"
          ></el-alert>

          <section class="section-block">
            <div class="section-title">Run 分段</div>
            <el-table :data="plan.runs || []" v-loading="planLoading">
              <el-table-column prop="run_index" label="Run" width="80">
                <template v-slot="scope">R{{ scope.row.run_index }}</template>
              </el-table-column>
              <el-table-column
                prop="task_count"
                label="Task"
                width="100"
              ></el-table-column>
              <el-table-column
                prop="batch_count"
                label="Batch"
                width="90"
              ></el-table-column>
              <el-table-column
                prop="site_count"
                label="站点"
                width="90"
              ></el-table-column>
              <el-table-column
                prop="mixed_batch_count"
                label="混合尾批"
                width="110"
              ></el-table-column>
              <el-table-column label="站点ID" min-width="180">
                <template v-slot="scope">
                  {{ (scope.row.site_ids || []).join("、") || "-" }}
                </template>
              </el-table-column>
              <el-table-column label="预计费用" width="140">
                <template v-slot="scope">
                  {{ formatUsd(scope.row.estimated_cost) }}
                </template>
              </el-table-column>
            </el-table>
          </section>

          <section class="section-block">
            <div class="section-title">站点计划</div>
            <el-table :data="plan.sites || []" v-loading="planLoading">
              <el-table-column label="站点" min-width="210">
                <template v-slot="scope">
                  <div>
                    {{ scope.row.site_name || "站点ID " + scope.row.site_id }}
                  </div>
                  <div class="muted">{{ scope.row.site_url || "-" }}</div>
                </template>
              </el-table-column>
              <el-table-column
                prop="keyword_count"
                label="关键词"
                width="90"
              ></el-table-column>
              <el-table-column
                prop="full_block_count"
                label="整批数"
                width="90"
              ></el-table-column>
              <el-table-column
                prop="remainder_count"
                label="余数"
                width="80"
              ></el-table-column>
              <el-table-column label="所属Run" width="140">
                <template v-slot="scope">
                  {{ runIndexesText(scope.row.run_indexes) }}
                </template>
              </el-table-column>
              <el-table-column label="关键词样例" min-width="300">
                <template v-slot="scope">
                  <span
                    v-for="item in scope.row.keyword_samples || []"
                    :key="item.id"
                    class="sample-chip"
                  >
                    {{ item.keyword }}
                  </span>
                  <span v-if="!(scope.row.keyword_samples || []).length"
                    >-</span
                  >
                </template>
              </el-table-column>
            </el-table>
          </section>
        </el-tab-pane>

        <el-tab-pane label="执行总览" name="execution">
          <div v-loading="executionLoading" class="execution-wrap">
            <template v-if="current.has_cycle">
              <div class="cycle-head">
                <div>
                  <div class="cycle-no">
                    {{ currentCycle.cycle_no || "Cycle #" + currentCycle.id }}
                    <el-tag
                      size="small"
                      :type="statusTagType(currentCycle.status)"
                    >
                      {{ statusText(currentCycle.status) }}
                    </el-tag>
                    <el-tag
                      v-if="currentCycle.is_active"
                      size="small"
                      type="primary"
                    >
                      当前活动
                    </el-tag>
                  </div>
                  <div class="muted cycle-meta">
                    {{ cycleTypeText(currentCycle.cycle_type) }} ·
                    {{ currentCycle.period_key || "手动" }} · 开始
                    {{ timeText(currentCycle.started_at) }}
                  </div>
                </div>
                <div class="cycle-actions">
                  <el-button
                    v-if="
                      canResolve && Number(currentCycle.unknown_count || 0) > 0
                    "
                    size="small"
                    type="warning"
                    :loading="reconcileLoading"
                    @click="reconcileUnknown(currentCycle)"
                  >
                    提交未知对账
                  </el-button>
                  <el-button
                    v-if="
                      isRootAdmin && Number(currentCycle.unknown_count || 0) > 0
                    "
                    size="small"
                    type="danger"
                    plain
                    @click="openAbandon(currentCycle, currentUnknown)"
                  >
                    确认未落地
                  </el-button>
                  <el-button
                    v-if="
                      canResolve &&
                      isRepairableCycle(currentCycle) &&
                      Number(currentCycle.failed_count || 0) > 0
                    "
                    size="small"
                    type="warning"
                    plain
                    @click="openRepair(currentCycle)"
                  >
                    修复失败任务
                  </el-button>
                  <el-button
                    v-if="canResolve && currentCycle.status === 'blocked'"
                    size="small"
                    :loading="resumeLoading"
                    @click="resumeCycle(currentCycle)"
                  >
                    恢复Cycle
                  </el-button>
                  <el-button
                    v-if="isRootAdmin && currentCycle.is_active"
                    size="small"
                    type="danger"
                    plain
                    @click="openForceClose(currentCycle)"
                  >
                    强制收口
                  </el-button>
                </div>
              </div>

              <el-progress
                :percentage="Number(current.progress || 0)"
                :status="progressStatus(currentCycle.status)"
              ></el-progress>
              <div class="progress-caption">
                终态 {{ currentCycle.terminal_count || 0 }} /
                {{ currentCycle.keyword_count || 0 }}；已发布站点
                {{ currentCycle.published_site_count || 0 }} /
                {{ currentCycle.site_count || 0 }}
              </div>

              <el-alert
                v-if="currentCycle.error_message"
                class="section-gap"
                type="warning"
                :closable="false"
                show-icon
                :title="currentCycle.error_message"
              ></el-alert>

              <el-alert
                v-if="Number(currentUnknown.unknown_count || 0) > 0"
                class="section-gap"
                type="warning"
                :closable="false"
                show-icon
                :title="unknownResolutionTitle(currentUnknown)"
                :description="unknownResolutionDescription(currentUnknown)"
              ></el-alert>

              <div class="metric-grid compact">
                <div class="metric-card">
                  <span>已提交</span>
                  <strong>{{ currentCycle.submitted_count || 0 }}</strong>
                </div>
                <div class="metric-card">
                  <span>已采集</span>
                  <strong>{{ currentCycle.collected_count || 0 }}</strong>
                </div>
                <div class="metric-card">
                  <span>有排名</span>
                  <strong>{{ currentCycle.success_count || 0 }}</strong>
                </div>
                <div class="metric-card">
                  <span>未找到</span>
                  <strong>{{ currentCycle.not_found_count || 0 }}</strong>
                </div>
                <div class="metric-card">
                  <span>最终失败</span>
                  <strong>{{ currentCycle.failed_count || 0 }}</strong>
                </div>
                <div class="metric-card">
                  <span>提交未知</span>
                  <strong>{{ currentCycle.unknown_count || 0 }}</strong>
                </div>
                <div class="metric-card">
                  <span>重试轮次</span>
                  <strong>
                    {{ currentCycle.retry_round || 0 }} /
                    {{ currentCycle.max_retry_round || 0 }}
                  </strong>
                </div>
                <div class="metric-card">
                  <span>付费POST费用</span>
                  <strong>{{ formatUsd(currentCycle.total_cost) }}</strong>
                </div>
              </div>

              <div v-if="statusCountEntries.length" class="status-chips">
                <el-tag
                  v-for="item in statusCountEntries"
                  :key="item.status"
                  size="small"
                  :type="statusTagType(item.status)"
                >
                  {{ statusText(item.status) }} {{ item.count }}
                </el-tag>
              </div>

              <section class="section-block">
                <div class="section-title">Run 执行状态</div>
                <run-table
                  :rows="current.runs || []"
                  @open-batches="openBatches"
                ></run-table>
              </section>

              <section class="section-block">
                <div class="section-title">站点发布门闩</div>
                <site-cycle-table
                  :rows="current.sites || []"
                  :can-repair="canResolve && isRepairableCycle(currentCycle)"
                  @repair="openRepair(currentCycle, $event)"
                ></site-cycle-table>
              </section>

              <section class="section-block">
                <div class="section-title">最近执行日志</div>
                <sync-log-table :rows="current.logs || []"></sync-log-table>
              </section>
            </template>
            <el-empty
              v-else
              description="暂无Cycle；计划日期到达后由调度器自动创建"
            ></el-empty>
          </div>
        </el-tab-pane>

        <el-tab-pane label="执行记录" name="history">
          <el-form :model="cycleParams" size="small" inline class="filter-form">
            <el-form-item label="类型">
              <el-select
                v-model="cycleParams.cycle_type"
                clearable
                placeholder="全部"
                class="filter-select"
              >
                <el-option label="全站月度" value="auto"></el-option>
                <el-option label="手动单站" value="manual"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="状态">
              <el-select
                v-model="cycleParams.status"
                clearable
                placeholder="全部"
                class="filter-select"
              >
                <el-option
                  v-for="item in cycleStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="周期">
              <el-input
                v-model.trim="cycleParams.period_key"
                clearable
                placeholder="YYYY-MM"
                class="filter-select"
              ></el-input>
            </el-form-item>
            <el-form-item label="搜索">
              <el-input
                v-model.trim="cycleParams.keyword"
                clearable
                placeholder="Cycle编号/账号/异常"
                class="filter-keyword"
                @keyup.enter.native="loadCycles(1)"
              ></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="loadCycles(1)">查询</el-button>
              <el-button @click="resetCycleFilters">重置</el-button>
            </el-form-item>
          </el-form>

          <el-table :data="cyclePager.list" v-loading="cyclePager.loading">
            <el-table-column label="Cycle" min-width="180">
              <template v-slot="scope">
                <div>{{ scope.row.cycle_no || scope.row.id }}</div>
                <div class="muted">
                  {{ cycleTypeText(scope.row.cycle_type) }} ·
                  {{ scope.row.period_key || "手动" }}
                </div>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="120">
              <template v-slot="scope">
                <el-tag size="small" :type="statusTagType(scope.row.status)">
                  {{ statusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="进度" width="170">
              <template v-slot="scope">
                <el-progress
                  :percentage="Number(scope.row.progress || 0)"
                  :show-text="true"
                ></el-progress>
              </template>
            </el-table-column>
            <el-table-column label="站点/Task" width="120">
              <template v-slot="scope">
                {{ scope.row.site_count || 0 }} /
                {{ scope.row.keyword_count || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="Run/Batch" width="120">
              <template v-slot="scope">
                {{ scope.row.run_count || 0 }} /
                {{ scope.row.batch_count || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="发布站点" width="100">
              <template v-slot="scope">
                {{ scope.row.published_site_count || 0 }} /
                {{ scope.row.site_count || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="费用" width="130">
              <template v-slot="scope">
                {{ formatUsd(scope.row.total_cost) }}
              </template>
            </el-table-column>
            <el-table-column label="开始/完成" min-width="180">
              <template v-slot="scope">
                <div>{{ timeText(scope.row.started_at) }}</div>
                <div class="muted">{{ timeText(scope.row.finished_at) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90" fixed="right">
              <template v-slot="scope">
                <el-button
                  type="text"
                  size="small"
                  @click="openCycle(scope.row)"
                >
                  详情
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="pagination-row">
            <pagination
              v-model="cyclePager"
              :page-sizes="[10, 20, 50]"
              @change="loadCycles()"
            ></pagination>
          </div>

          <section class="section-block legacy-block">
            <div class="section-title">
              历史 Run（Cycle 上线前）
              <span class="section-note">cycle_id 为空，仅保留只读查询</span>
            </div>
            <run-table
              :rows="legacyRunPager.list"
              :loading="legacyRunPager.loading"
              legacy
              @open-batches="openBatches"
            ></run-table>
            <div class="pagination-row">
              <pagination
                v-model="legacyRunPager"
                :page-sizes="[10, 20, 50]"
                @change="loadLegacyRuns()"
              ></pagination>
            </div>
          </section>
        </el-tab-pane>

        <el-tab-pane label="运行维护" name="ops">
          <div v-loading="healthLoading">
            <div class="metric-grid compact">
              <div class="metric-card">
                <span>待提交</span>
                <strong>{{ health.pending_count || 0 }}</strong>
              </div>
              <div class="metric-card">
                <span>采集中</span>
                <strong>{{ health.submitted_count || 0 }}</strong>
              </div>
              <div class="metric-card">
                <span>提交未知</span>
                <strong>{{ health.submission_unknown_count || 0 }}</strong>
              </div>
              <div class="metric-card">
                <span>等待重试</span>
                <strong>{{ health.retry_pending_count || 0 }}</strong>
              </div>
              <div class="metric-card">
                <span>活动Run</span>
                <strong>{{ health.running_run_count || 0 }}</strong>
              </div>
              <div class="metric-card">
                <span>活动Batch</span>
                <strong>{{ health.running_batch_count || 0 }}</strong>
              </div>
              <div class="metric-card">
                <span>今日提交</span>
                <strong>{{ health.today_submitted_tasks || 0 }}</strong>
              </div>
              <div class="metric-card">
                <span>今日付费POST</span>
                <strong>{{ formatUsd(health.today_cost) }}</strong>
              </div>
              <div class="metric-card">
                <span>等待状态</span>
                <strong>{{ health.waiting_state_text || "-" }}</strong>
              </div>
              <div class="metric-card">
                <span>最早等待时长</span>
                <strong>{{
                  waitingDurationText(health.waiting_seconds)
                }}</strong>
              </div>
            </div>

            <section class="section-block">
              <div class="section-title">月度与采集配置</div>
              <el-descriptions :column="3" border size="small">
                <el-descriptions-item label="月度查询">
                  每月 {{ autoConfig.monthly_refresh_day || 5 }} 日
                  {{ autoConfig.monthly_refresh_time || "00:00" }}
                </el-descriptions-item>
                <el-descriptions-item label="时区">
                  {{ autoConfig.monthly_timezone || "Asia/Shanghai" }}
                </el-descriptions-item>
                <el-descriptions-item label="单Run上限">
                  {{ autoConfig.max_keywords_per_run || 0 }}
                </el-descriptions-item>
                <el-descriptions-item label="Cycle批量">
                  {{ autoConfig.standard_batch_size || 100 }}
                </el-descriptions-item>
                <el-descriptions-item label="单轮采集">
                  {{ autoConfig.collect_max_tasks_per_run || 0 }} Task /
                  {{ autoConfig.collect_max_api_calls_per_run || 0 }} API
                </el-descriptions-item>
                <el-descriptions-item label="采集并发/预算">
                  {{ autoConfig.collect_concurrency || 0 }} /
                  {{ autoConfig.collect_time_budget_seconds || 0 }}秒
                </el-descriptions-item>
                <el-descriptions-item label="提交预算/回调">
                  {{ autoConfig.submit_time_budget_seconds || 240 }}秒 /
                  {{ autoConfig.callback_mode || "polling" }}
                </el-descriptions-item>
              </el-descriptions>
            </section>

            <section class="section-block">
              <div class="section-title">命令心跳</div>
              <div class="heartbeat-grid">
                <div class="heartbeat-card">
                  <span>seo:check-rank</span>
                  <strong>{{
                    commandStatusText(health.last_check_run)
                  }}</strong>
                  <small>{{
                    timeText(
                      health.last_check_run && health.last_check_run.finished_at
                    )
                  }}</small>
                </div>
                <div class="heartbeat-card">
                  <span>seo:collect-rank</span>
                  <strong>{{
                    commandStatusText(health.last_collect_run)
                  }}</strong>
                  <small>{{
                    timeText(
                      health.last_collect_run &&
                        health.last_collect_run.finished_at
                    )
                  }}</small>
                </div>
                <div class="heartbeat-card">
                  <span>最早等待任务</span>
                  <strong>{{ timeText(health.oldest_submitted_at) }}</strong>
                  <small>下次重试 {{ timeText(health.next_retry_at) }}</small>
                </div>
                <div class="heartbeat-card">
                  <span>seo:cleanup-logs</span>
                  <strong>{{
                    commandStatusText(health.last_cleanup_run)
                  }}</strong>
                  <small>{{
                    timeText(
                      health.last_cleanup_run &&
                        health.last_cleanup_run.finished_at
                    )
                  }}</small>
                </div>
              </div>
            </section>

            <section class="section-block">
              <div class="section-title">日志清理预览（当前页面不会删除）</div>
              <el-descriptions :column="3" border size="small">
                <el-descriptions-item label="删除成功/跳过日志">
                  {{ cleanupPreview.delete_success_logs || 0 }}
                </el-descriptions-item>
                <el-descriptions-item label="删除异常日志">
                  {{ cleanupPreview.delete_failed_logs || 0 }}
                </el-descriptions-item>
                <el-descriptions-item label="删除命令日志">
                  {{ cleanupPreview.delete_command_success_runs || 0 }}
                </el-descriptions-item>
                <el-descriptions-item label="删除失败命令日志">
                  {{ cleanupPreview.delete_command_failed_runs || 0 }}
                </el-descriptions-item>
                <el-descriptions-item label="清空API Raw">
                  {{ cleanupPreview.clear_api_raw_response || 0 }}
                </el-descriptions-item>
                <el-descriptions-item label="清空Snapshot Raw">
                  {{ cleanupPreview.clear_snapshot_raw_response || 0 }}
                </el-descriptions-item>
                <el-descriptions-item label="保留口径">
                  成功{{ cleanupPreview.success_days || 0 }}天 / 异常{{
                    cleanupPreview.failed_days || 0
                  }}天
                </el-descriptions-item>
              </el-descriptions>
              <div class="ops-note">{{ cleanupPreview.note || "" }}</div>
            </section>
          </div>

          <section class="section-block">
            <div class="section-title">命令运行记录</div>
            <el-form :model="commandParams" size="small" inline>
              <el-form-item label="命令">
                <el-select
                  v-model="commandParams.command_name"
                  clearable
                  placeholder="全部"
                  class="command-select"
                >
                  <el-option
                    label="seo:check-rank"
                    value="seo:check-rank"
                  ></el-option>
                  <el-option
                    label="seo:collect-rank"
                    value="seo:collect-rank"
                  ></el-option>
                  <el-option
                    label="seo:cleanup-logs"
                    value="seo:cleanup-logs"
                  ></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="状态">
                <el-select
                  v-model="commandParams.status"
                  clearable
                  placeholder="全部"
                  class="filter-select"
                >
                  <el-option label="成功" value="success"></el-option>
                  <el-option label="跳过" value="skipped"></el-option>
                  <el-option label="失败" value="failed"></el-option>
                  <el-option label="运行中" value="running"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="loadCommandRuns(1)">
                  查询
                </el-button>
              </el-form-item>
            </el-form>
            <el-table
              :data="commandPager.list"
              v-loading="commandPager.loading"
            >
              <el-table-column
                prop="command_name"
                label="命令"
                width="180"
              ></el-table-column>
              <el-table-column label="状态" width="100">
                <template v-slot="scope">
                  <el-tag size="small" :type="commandTagType(scope.row.status)">
                    {{ commandStatusLabel(scope.row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column
                prop="message"
                label="结果"
                min-width="260"
                show-overflow-tooltip
              ></el-table-column>
              <el-table-column label="开始" width="170">
                <template v-slot="scope">
                  {{ timeText(scope.row.started_at || scope.row.create_time) }}
                </template>
              </el-table-column>
              <el-table-column label="耗时" width="100">
                <template v-slot="scope">
                  {{ durationText(scope.row.duration_ms) }}
                </template>
              </el-table-column>
            </el-table>
            <div class="pagination-row">
              <pagination
                v-model="commandPager"
                :page-sizes="[10, 20, 50]"
                @change="loadCommandRuns()"
              ></pagination>
            </div>
          </section>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-drawer
      :visible.sync="cycleDrawer.visible"
      :title="cycleDrawer.title"
      size="86%"
      append-to-body
      @close="onCycleDrawerClose"
    >
      <div class="drawer-body" v-loading="cycleDetailLoading">
        <template v-if="cycleDetail.cycle">
          <el-descriptions :column="4" border size="small">
            <el-descriptions-item label="Cycle">
              {{ cycleDetail.cycle.cycle_no }}
            </el-descriptions-item>
            <el-descriptions-item label="状态">
              {{ statusText(cycleDetail.cycle.status) }}
            </el-descriptions-item>
            <el-descriptions-item label="周期">
              {{ cycleDetail.cycle.period_key || "手动" }}
            </el-descriptions-item>
            <el-descriptions-item label="费用">
              {{ formatUsd(cycleDetail.cycle.total_cost) }}
            </el-descriptions-item>
          </el-descriptions>
          <div class="cycle-detail-actions">
            <el-button
              v-if="
                canResolve &&
                Number(
                  (cycleDetail.unknown_resolution || {}).unknown_count || 0
                ) > 0
              "
              size="small"
              type="warning"
              :loading="reconcileLoading"
              @click="reconcileUnknown(cycleDetail.cycle)"
            >
              提交未知对账
            </el-button>
            <el-button
              v-if="
                isRootAdmin &&
                Number(
                  (cycleDetail.unknown_resolution || {}).unknown_count || 0
                ) > 0
              "
              size="small"
              type="danger"
              plain
              @click="
                openAbandon(
                  cycleDetail.cycle,
                  cycleDetail.unknown_resolution || {}
                )
              "
            >
              确认未落地
            </el-button>
            <el-button
              v-if="
                canResolve &&
                isRepairableCycle(cycleDetail.cycle) &&
                Number(cycleDetail.cycle.failed_count || 0) > 0
              "
              size="small"
              type="warning"
              plain
              @click="openRepair(cycleDetail.cycle)"
            >
              修复失败任务
            </el-button>
          </div>
          <el-alert
            v-if="
              Number(
                (cycleDetail.unknown_resolution || {}).unknown_count || 0
              ) > 0
            "
            class="section-gap"
            type="warning"
            :closable="false"
            show-icon
            :title="
              unknownResolutionTitle(cycleDetail.unknown_resolution || {})
            "
            :description="
              unknownResolutionDescription(cycleDetail.unknown_resolution || {})
            "
          ></el-alert>
          <section class="section-block">
            <div class="section-title">Run</div>
            <run-table
              :rows="cycleDetail.runs || []"
              @open-batches="openBatches"
            ></run-table>
          </section>
          <section class="section-block">
            <div class="section-title">站点发布</div>
            <site-cycle-table
              :rows="cycleDetail.sites || []"
              :can-repair="canResolve && isRepairableCycle(cycleDetail.cycle)"
              @repair="openRepair(cycleDetail.cycle, $event)"
            ></site-cycle-table>
          </section>
          <section class="section-block">
            <div class="section-title">Cycle 时间线</div>
            <el-timeline v-if="cycleTimeline.length">
              <el-timeline-item
                v-for="(event, index) in cycleTimeline"
                :key="event.time + '-' + event.type + '-' + index"
                :timestamp="timeText(event.time)"
                :type="timelineTagType(event.level)"
                placement="top"
              >
                <strong>{{ event.title }}</strong>
                <div class="muted timeline-desc">{{ event.desc }}</div>
              </el-timeline-item>
            </el-timeline>
            <el-empty v-else description="暂无时间线"></el-empty>
          </section>
        </template>
      </div>
    </el-drawer>

    <el-drawer
      :visible.sync="batchDrawer.visible"
      :title="batchDrawer.title"
      size="78%"
      append-to-body
    >
      <div class="drawer-body">
        <el-table :data="batchPager.list" v-loading="batchPager.loading">
          <el-table-column
            prop="global_batch_index"
            label="#"
            width="60"
          ></el-table-column>
          <el-table-column
            prop="batch_no"
            label="Batch"
            min-width="150"
          ></el-table-column>
          <el-table-column label="站点" min-width="150">
            <template v-slot="scope">
              {{ scope.row.site_name || "混合尾批" }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110">
            <template v-slot="scope">
              <el-tag size="small" :type="statusTagType(scope.row.status)">
                {{ statusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="task_count"
            label="Task"
            width="80"
          ></el-table-column>
          <el-table-column label="提交/采集" width="110">
            <template v-slot="scope">
              {{ scope.row.submitted_count || 0 }} /
              {{ scope.row.collected_count || 0 }}
            </template>
          </el-table-column>
          <el-table-column label="成功/失败" width="110">
            <template v-slot="scope">
              {{ scope.row.success_count || 0 }} /
              {{ scope.row.failed_count || 0 }}
            </template>
          </el-table-column>
          <el-table-column label="POST次数" width="90">
            <template v-slot="scope">
              {{ scope.row.submit_attempt_count || 0 }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" fixed="right">
            <template v-slot="scope">
              <el-button type="text" size="small" @click="openTasks(scope.row)">
                Task
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-row">
          <pagination
            v-model="batchPager"
            :page-sizes="[10, 20, 50]"
            @change="loadBatches()"
          ></pagination>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      :visible.sync="taskDrawer.visible"
      :title="taskDrawer.title"
      size="88%"
      append-to-body
    >
      <div class="drawer-body">
        <el-table :data="taskPager.list" v-loading="taskPager.loading">
          <el-table-column
            prop="keyword"
            label="关键词"
            min-width="180"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column
            prop="site_name"
            label="站点"
            min-width="130"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column label="状态" width="120">
            <template v-slot="scope">
              <el-tag size="small" :type="statusTagType(scope.row.status)">
                {{ statusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="排名" width="80">
            <template v-slot="scope">
              {{ scope.row.rank_group === null ? "-" : scope.row.rank_group }}
            </template>
          </el-table-column>
          <el-table-column
            prop="provider_task_id"
            label="Provider Task ID"
            min-width="180"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column label="回调" width="120">
            <template v-slot="scope">
              {{ scope.row.callback_status || scope.row.callback_mode || "-" }}
            </template>
          </el-table-column>
          <el-table-column label="重试" width="120">
            <template v-slot="scope">
              {{ scope.row.resubmit_count || 0 }} /
              {{ scope.row.max_retry_count || 0 }}
            </template>
          </el-table-column>
          <el-table-column
            prop="last_error_message"
            label="异常"
            min-width="220"
            show-overflow-tooltip
          ></el-table-column>
          <el-table-column label="费用" width="120">
            <template v-slot="scope">
              {{ formatUsd(scope.row.cost) }}
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-row">
          <pagination
            v-model="taskPager"
            :page-sizes="[10, 20, 50]"
            @change="loadTasks()"
          ></pagination>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      title="超级管理员确认提交未落地"
      :visible.sync="abandonDialog.visible"
      width="620px"
      append-to-body
    >
      <el-alert
        type="error"
        :closable="false"
        show-icon
        title="本操作不会重新POST；仅在45分钟安全窗、至少3次成功空对账且等待期后再次对账均满足时可执行。"
      ></el-alert>
      <el-descriptions class="dialog-metrics" :column="2" border size="small">
        <el-descriptions-item label="Cycle">
          {{ abandonDialog.cycleNo || "-" }}
        </el-descriptions-item>
        <el-descriptions-item label="安全状态">
          {{ unknownStateText(abandonDialog.resolution.state) }}
        </el-descriptions-item>
        <el-descriptions-item label="未知任务">
          {{ abandonDialog.resolution.unknown_count || 0 }}
        </el-descriptions-item>
        <el-descriptions-item label="最少对账次数">
          {{ abandonDialog.resolution.min_reconcile_attempts || 0 }} /
          {{ abandonDialog.resolution.required_reconcile_attempts || 3 }}
        </el-descriptions-item>
        <el-descriptions-item label="最早允许时间">
          {{ timeText(abandonDialog.resolution.eligible_at) }}
        </el-descriptions-item>
        <el-descriptions-item label="最后对账">
          {{ timeText(abandonDialog.resolution.last_reconciled_at) }}
        </el-descriptions-item>
      </el-descriptions>
      <el-form
        class="force-form"
        :model="abandonForm"
        label-width="110px"
        size="small"
      >
        <el-form-item label="Cycle编号">
          <el-input
            v-model.trim="abandonForm.cycle_no"
            :placeholder="abandonDialog.cycleNo"
          ></el-input>
        </el-form-item>
        <el-form-item label="确认原因">
          <el-input
            v-model.trim="abandonForm.reason"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="abandonDialog.visible = false">
          取消
        </el-button>
        <el-button
          size="small"
          type="danger"
          :loading="abandonLoading"
          :disabled="
            !Number(abandonDialog.resolution.can_abandon || 0) ||
            abandonForm.cycle_no !== abandonDialog.cycleNo ||
            !abandonForm.reason
          "
          @click="abandonUnknown"
        >
          确认未落地（不触发POST）
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      title="修复已收口Cycle的失败任务"
      :visible.sync="repairDialog.visible"
      width="680px"
      append-to-body
      :close-on-click-modal="!repairLoading"
      :close-on-press-escape="!repairLoading"
      :show-close="!repairLoading"
      @close="onRepairDialogClose"
    >
      <div v-loading="repairPreviewLoading">
        <el-alert
          v-if="repairPreviewState === 'loading'"
          type="info"
          :closable="false"
          show-icon
          title="正在核对失败任务、付费数量和当日预算，请稍候。"
        ></el-alert>
        <el-alert
          v-else-if="repairPreviewState === 'failed'"
          type="error"
          :closable="false"
          show-icon
          title="修复预览加载失败，当前禁止提交。"
          :description="repairPreviewError || '请关闭弹窗后重新打开预览。'"
        ></el-alert>
        <el-alert
          v-else-if="repairPreviewState === 'paid'"
          type="error"
          :closable="false"
          show-icon
          :title="`本次将触发付费POST：${
            repairPreview.paid_resubmit_count || 0
          }个任务，预计${formatUsd(repairPreview.estimated_cost)}`"
          description="提交前会再次校验预算、活动Cycle和提交未知状态；请求超时或响应丢失时进入ID List对账，禁止重复点击。"
        ></el-alert>
        <el-alert
          v-else-if="repairPreviewState === 'free'"
          :type="Number(repairPreview.task_count || 0) > 0 ? 'success' : 'info'"
          :closable="false"
          show-icon
          :title="
            Number(repairPreview.task_count || 0) > 0
              ? '本次只重新采集已有Provider Task ID，不触发付费POST。'
              : '当前范围没有可人工修复的失败任务。'
          "
        ></el-alert>
        <el-descriptions
          v-if="isRepairPreviewReady"
          class="dialog-metrics"
          :column="3"
          border
          size="small"
        >
          <el-descriptions-item label="范围">
            {{ repairDialog.siteName || "整个Cycle" }}
          </el-descriptions-item>
          <el-descriptions-item label="失败Task">
            {{ repairPreview.task_count || 0 }}
          </el-descriptions-item>
          <el-descriptions-item label="站点/Batch">
            {{ repairPreview.site_count || 0 }} /
            {{ repairPreview.batch_count || 0 }}
          </el-descriptions-item>
          <el-descriptions-item label="免费采集">
            {{ repairPreview.free_collect_count || 0 }}
          </el-descriptions-item>
          <el-descriptions-item label="付费重提">
            {{ repairPreview.paid_resubmit_count || 0 }}
          </el-descriptions-item>
          <el-descriptions-item label="预计费用">
            {{ formatUsd(repairPreview.estimated_cost) }}
          </el-descriptions-item>
          <el-descriptions-item label="今日已用">
            {{ formatUsd(repairPreview.today_cost) }}
          </el-descriptions-item>
          <el-descriptions-item label="每日预算">
            {{ budgetText(repairPreview.daily_budget) }}
          </el-descriptions-item>
          <el-descriptions-item label="剩余预算">
            {{ budgetText(repairPreview.remaining_budget, true) }}
          </el-descriptions-item>
        </el-descriptions>
        <el-form
          class="force-form"
          :model="repairForm"
          label-width="110px"
          size="small"
        >
          <el-form-item label="Cycle编号">
            <el-input
              v-model.trim="repairForm.cycle_no"
              :placeholder="repairDialog.cycleNo"
            ></el-input>
          </el-form-item>
          <el-form-item label="修复原因">
            <el-input
              v-model.trim="repairForm.reason"
              type="textarea"
              :rows="4"
              maxlength="500"
              show-word-limit
            ></el-input>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <el-button
          size="small"
          :disabled="repairLoading"
          @click="repairDialog.visible = false"
        >
          取消
        </el-button>
        <el-button
          size="small"
          :type="
            Number(repairPreview.paid_resubmit_count || 0) > 0
              ? 'danger'
              : 'primary'
          "
          :loading="repairLoading"
          :disabled="
            repairPreviewLoading ||
            !isRepairPreviewReady ||
            !Number(repairPreview.task_count || 0) ||
            repairForm.cycle_no !== repairDialog.cycleNo ||
            !repairForm.reason
          "
          @click="repairFailed"
        >
          {{
            Number(repairPreview.paid_resubmit_count || 0) > 0
              ? "确认付费修复"
              : "确认免费采集"
          }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      title="超级管理员强制收口"
      :visible.sync="forceCloseDialog.visible"
      width="560px"
      append-to-body
    >
      <el-alert
        type="error"
        :closable="false"
        show-icon
        title="仅用于没有远端未完成任务、没有提交未知和有效执行租约的异常Cycle。"
      ></el-alert>
      <el-form
        class="force-form"
        :model="forceCloseForm"
        label-width="110px"
        size="small"
      >
        <el-form-item label="Cycle编号">
          <el-input
            v-model.trim="forceCloseForm.cycle_no"
            :placeholder="forceCloseDialog.cycleNo"
          ></el-input>
        </el-form-item>
        <el-form-item label="收口原因">
          <el-input
            v-model.trim="forceCloseForm.reason"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="forceCloseDialog.visible = false">
          取消
        </el-button>
        <el-button
          size="small"
          type="danger"
          :loading="forceCloseLoading"
          @click="forceCloseCycle"
        >
          确认强制收口
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Pagination from "@/components/pagination";
import { Pager } from "@/utils/pager";
import { formatDate } from "@/utils/util";
import {
  apiSeoRankAbandonUnknown,
  apiSeoRankAutoBatchList,
  apiSeoRankAutoRunList,
  apiSeoRankAutoTaskList,
  apiSeoRankCleanupPreview,
  apiSeoRankCommandRunList,
  apiSeoRankCurrentExecution,
  apiSeoRankCycleDetail,
  apiSeoRankCycleList,
  apiSeoRankCycleTimeline,
  apiSeoRankForceCloseCycle,
  apiSeoRankManualCollect,
  apiSeoRankReconcileUnknown,
  apiSeoRankRepairFailed,
  apiSeoRankRepairPreview,
  apiSeoRankResumeCycle,
  apiSeoRankRunHealth,
  apiSeoRankRunPlan,
} from "@/api/seo";

const statusMap = {
  planning: "规划中",
  planned: "已规划",
  queued: "待提交",
  pending: "待提交",
  submitting: "提交中",
  submitted: "等待采集",
  processing: "第三方处理中",
  collecting: "采集中",
  retrying: "重试中",
  retry_pending: "等待重试",
  submission_unknown: "提交状态未知",
  blocked: "已阻断",
  ready_to_publish: "待发布",
  published: "已发布",
  published_partial: "部分发布",
  repairing: "修复中",
  completed: "已完成",
  partial_failed: "部分失败",
  failed: "失败",
  timeout: "超时",
  expired: "已过期",
  force_closed: "已强制收口",
  success: "成功",
  skipped: "已跳过",
  running: "运行中",
};

const terminalCycleStatuses = [
  "completed",
  "partial_failed",
  "failed",
  "force_closed",
];

const RunTable = {
  functional: true,
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    legacy: { type: Boolean, default: false },
  },
  render(h, context) {
    const parent = context.parent;
    const column = (props, scopedSlots) =>
      h("el-table-column", { props, scopedSlots });
    return h(
      "el-table",
      {
        props: { data: context.props.rows },
        directives: [{ name: "loading", value: context.props.loading }],
      },
      [
        column(
          { label: context.props.legacy ? "历史Run" : "Run", minWidth: 170 },
          {
            default: ({ row }) =>
              h("div", [
                h("div", row.run_no || String(row.id)),
                h(
                  "div",
                  { class: "muted" },
                  context.props.legacy
                    ? "历史链路"
                    : "R" + (row.run_index || row.id)
                ),
              ]),
          }
        ),
        column(
          { label: "状态", width: 110 },
          {
            default: ({ row }) =>
              h(
                "el-tag",
                {
                  props: {
                    size: "small",
                    type: parent.statusTagType(row.status),
                  },
                },
                parent.statusText(row.status)
              ),
          }
        ),
        column({ label: "Task", width: 90, prop: "keyword_count" }),
        column({ label: "Batch", width: 80, prop: "batch_count" }),
        column(
          { label: "提交/采集", width: 120 },
          {
            default: ({ row }) =>
              `${row.submitted_count || 0} / ${row.collected_count || 0}`,
          }
        ),
        column(
          { label: "成功/失败", width: 120 },
          {
            default: ({ row }) =>
              `${row.success_count || 0} / ${row.failed_count || 0}`,
          }
        ),
        column(
          { label: "费用", width: 130 },
          {
            default: ({ row }) => parent.formatUsd(row.total_cost),
          }
        ),
        column(
          { label: "开始/完成", minWidth: 180 },
          {
            default: ({ row }) =>
              h("div", [
                h("div", parent.timeText(row.started_at || row.create_time)),
                h("div", { class: "muted" }, parent.timeText(row.finished_at)),
              ]),
          }
        ),
        column(
          { label: "操作", width: 80, fixed: "right" },
          {
            default: ({ row }) =>
              h(
                "el-button",
                {
                  props: { type: "text", size: "small" },
                  on: {
                    click: () =>
                      context.listeners["open-batches"] &&
                      context.listeners["open-batches"](row),
                  },
                },
                "Batch"
              ),
          }
        ),
      ]
    );
  },
};

const SiteCycleTable = {
  functional: true,
  props: {
    rows: { type: Array, default: () => [] },
    canRepair: { type: Boolean, default: false },
  },
  render(h, context) {
    const parent = context.parent;
    const column = (props, scopedSlots) =>
      h("el-table-column", { props, scopedSlots });
    const columns = [
      column(
        { label: "站点", minWidth: 190 },
        {
          default: ({ row }) =>
            h("div", [
              h("div", row.site_name || "站点ID " + row.site_id),
              h("div", { class: "muted" }, row.site_url || "-"),
            ]),
        }
      ),
      column(
        { label: "状态", width: 120 },
        {
          default: ({ row }) =>
            h(
              "el-tag",
              {
                props: {
                  size: "small",
                  type: parent.statusTagType(row.status),
                },
              },
              parent.statusText(row.status)
            ),
        }
      ),
      column(
        { label: "进度", minWidth: 150 },
        {
          default: ({ row }) =>
            h("el-progress", {
              props: { percentage: Number(row.progress || 0), showText: true },
            }),
        }
      ),
      column(
        { label: "计划/终态", width: 110 },
        {
          default: ({ row }) =>
            `${row.planned_count || 0} / ${row.terminal_count || 0}`,
        }
      ),
      column({ label: "发布快照", width: 100, prop: "published_count" }),
      column(
        { label: "成功/未找到/失败", width: 150 },
        {
          default: ({ row }) =>
            `${row.success_count || 0} / ${row.not_found_count || 0} / ${
              row.failed_count || 0
            }`,
        }
      ),
      column(
        { label: "发布时间", width: 170 },
        {
          default: ({ row }) => parent.timeText(row.published_at),
        }
      ),
    ];
    if (context.props.canRepair) {
      columns.push(
        column(
          { label: "操作", width: 80, fixed: "right" },
          {
            default: ({ row }) =>
              Number(row.failed_count || 0) > 0
                ? h(
                    "el-button",
                    {
                      props: { type: "text", size: "small" },
                      on: {
                        click: () =>
                          context.listeners.repair &&
                          context.listeners.repair(row),
                      },
                    },
                    "修复"
                  )
                : h("span", "-"),
          }
        )
      );
    }
    return h("el-table", { props: { data: context.props.rows } }, columns);
  },
};

const SyncLogTable = {
  functional: true,
  props: { rows: { type: Array, default: () => [] } },
  render(h, context) {
    const parent = context.parent;
    const column = (props, scopedSlots) =>
      h("el-table-column", { props, scopedSlots });
    return h(
      "el-table",
      { props: { data: context.props.rows, size: "small" } },
      [
        column(
          { label: "时间", width: 170 },
          {
            default: ({ row }) =>
              parent.timeText(row.finished_at || row.create_time),
          }
        ),
        column({ label: "动作", width: 180, prop: "action" }),
        column(
          { label: "状态", width: 110 },
          {
            default: ({ row }) =>
              h(
                "el-tag",
                {
                  props: {
                    size: "small",
                    type: parent.commandTagType(row.status),
                  },
                },
                parent.statusText(row.status)
              ),
          }
        ),
        column({
          label: "摘要",
          minWidth: 300,
          prop: "response_summary",
          showOverflowTooltip: true,
        }),
        column({
          label: "异常",
          minWidth: 200,
          prop: "error_message",
          showOverflowTooltip: true,
        }),
        column(
          { label: "费用", width: 120 },
          {
            default: ({ row }) => parent.formatUsd(row.cost),
          }
        ),
      ]
    );
  },
};

export default {
  components: {
    Pagination,
    RunTable,
    SiteCycleTable,
    SyncLogTable,
  },
  data() {
    return {
      activeTab: "plan",
      refreshing: false,
      planLoading: false,
      executionLoading: false,
      healthLoading: false,
      manualCollectLoading: false,
      reconcileLoading: false,
      abandonLoading: false,
      repairPreviewLoading: false,
      repairLoading: false,
      resumeLoading: false,
      forceCloseLoading: false,
      plan: { summary: {}, runs: [], sites: [] },
      current: {
        has_cycle: 0,
        cycle: {},
        sites: [],
        runs: [],
        status_counts: {},
        logs: [],
        unknown_resolution: {},
      },
      health: {},
      cleanupPreview: {},
      cycleParams: {
        cycle_type: "",
        status: "",
        period_key: "",
        keyword: "",
      },
      commandParams: {
        command_name: "",
        status: "",
      },
      cyclePager: new Pager({ size: 10 }),
      legacyRunPager: new Pager({ size: 10 }),
      commandPager: new Pager({ size: 10 }),
      batchPager: new Pager({ size: 10 }),
      taskPager: new Pager({ size: 10 }),
      selectedRun: null,
      selectedBatch: null,
      cycleDrawer: {
        visible: false,
        title: "Cycle详情",
      },
      batchDrawer: {
        visible: false,
        title: "Batch明细",
      },
      taskDrawer: {
        visible: false,
        title: "Task明细",
      },
      cycleDetailLoading: false,
      cycleDetailRequestSeq: 0,
      cycleDetailTargetId: 0,
      cycleDetail: {},
      cycleTimeline: [],
      abandonDialog: {
        visible: false,
        cycleId: 0,
        cycleNo: "",
        resolution: {},
      },
      abandonForm: {
        cycle_no: "",
        reason: "",
      },
      repairDialog: {
        visible: false,
        cycleId: 0,
        cycleNo: "",
        siteId: 0,
        siteName: "",
      },
      repairPreview: {},
      repairPreviewState: "idle",
      repairPreviewError: "",
      repairPreviewRequestSeq: 0,
      repairForm: {
        cycle_no: "",
        reason: "",
      },
      forceCloseDialog: {
        visible: false,
        cycleId: 0,
        cycleNo: "",
      },
      forceCloseForm: {
        cycle_no: "",
        reason: "",
      },
      pollTimer: null,
      pollInFlight: false,
      pollingStopped: false,
      lastHealthAt: 0,
    };
  },
  computed: {
    ...mapGetters(["permission"]),
    canSync() {
      return this.$hasPermission("seo.auto_run", "sync");
    },
    canResolve() {
      return (
        Number((this.permission && this.permission.root) || 0) === 1 ||
        this.$hasPermission("seo.auto_run", "resolve")
      );
    },
    isRootAdmin() {
      return Number((this.permission && this.permission.root) || 0) === 1;
    },
    planSummary() {
      return this.plan.summary || {};
    },
    currentCycle() {
      return this.current.cycle || {};
    },
    currentUnknown() {
      return this.current.unknown_resolution || {};
    },
    isRepairPreviewReady() {
      if (!["free", "paid"].includes(this.repairPreviewState)) return false;
      return (
        Number(this.repairPreview.cycle_id || 0) ===
          Number(this.repairDialog.cycleId || 0) &&
        Number(this.repairPreview.site_id || 0) ===
          Number(this.repairDialog.siteId || 0)
      );
    },
    autoConfig() {
      return this.health.auto_config || {};
    },
    statusCountEntries() {
      return Object.keys(this.current.status_counts || {})
        .map((status) => ({
          status,
          count: Number(this.current.status_counts[status] || 0),
        }))
        .filter((item) => item.count > 0);
    },
    cycleStatusOptions() {
      return [
        "submitting",
        "collecting",
        "retrying",
        "blocked",
        "completed",
        "partial_failed",
        "failed",
        "force_closed",
      ].map((value) => ({ value, label: this.statusText(value) }));
    },
    planDueText() {
      if (!this.plan.scheduled_at) return "等待计划数据";
      if (Number(this.plan.scheduled_at) <= Math.floor(Date.now() / 1000)) {
        return this.current.has_cycle
          ? "本月Cycle已存在"
          : "已到期，调度器将补跑本月";
      }
      return "尚未到计划时间";
    },
    planDueTagType() {
      if (!this.plan.scheduled_at) return "info";
      if (Number(this.plan.scheduled_at) <= Math.floor(Date.now() / 1000)) {
        return this.current.has_cycle ? "success" : "warning";
      }
      return "info";
    },
    planWarningText() {
      const messages = [];
      if (this.planSummary.invalid_count) {
        messages.push(
          `${this.planSummary.invalid_count}个关键词请求字段不完整，未纳入计划`
        );
      }
      if (this.planSummary.capacity_warning) {
        messages.push(
          `计划超过设计容量${this.planSummary.design_capacity || 10000}`
        );
      }
      return messages.join("；");
    },
  },
  created() {
    this.pollingStopped = false;
    this.loadPlan();
    this.loadHealth();
    this.loadCleanupPreview();
    this.loadCycles();
    this.loadLegacyRuns();
    this.loadCommandRuns();
    this.startPolling();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    this.pollingStopped = true;
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  methods: {
    refreshAll() {
      this.refreshing = true;
      const requests = [
        this.loadPlan(),
        this.loadCurrent(),
        this.loadHealth(),
        this.loadCleanupPreview(),
        this.loadCycles(),
        this.loadLegacyRuns(),
        this.loadCommandRuns(),
      ];
      const detailCycleId = this.openCycleDetailId();
      if (detailCycleId) {
        requests.push(this.reloadCycleDetail(detailCycleId));
      }
      Promise.all(requests)
        .catch(() => {})
        .finally(() => {
          this.refreshing = false;
        });
    },
    onTabChange(tab) {
      if (tab.name === "history") {
        this.loadCycles();
        this.loadLegacyRuns();
      }
      if (tab.name === "ops") {
        this.loadHealth();
        this.loadCleanupPreview();
        this.loadCommandRuns();
      }
    },
    loadPlan() {
      this.planLoading = true;
      return apiSeoRankRunPlan({})
        .then((resp) => {
          this.plan = resp || { summary: {}, runs: [], sites: [] };
        })
        .finally(() => {
          this.planLoading = false;
        });
    },
    loadCurrent(silent = false) {
      if (this.pollInFlight) return Promise.resolve(this.current);
      this.pollInFlight = true;
      if (!silent) this.executionLoading = true;
      const oldCycleId = Number((this.current.cycle || {}).id || 0);
      const oldStatus = (this.current.cycle || {}).status || "";
      return apiSeoRankCurrentExecution({})
        .then((resp) => {
          this.current = resp || { has_cycle: 0, cycle: {} };
          const newCycleId = Number((this.current.cycle || {}).id || 0);
          const newStatus = (this.current.cycle || {}).status || "";
          if (
            newCycleId !== oldCycleId ||
            (oldStatus !== newStatus &&
              terminalCycleStatuses.includes(newStatus))
          ) {
            this.loadCycles();
          }
          return this.current;
        })
        .finally(() => {
          this.pollInFlight = false;
          this.executionLoading = false;
        });
    },
    loadHealth() {
      this.healthLoading = true;
      return apiSeoRankRunHealth()
        .then((resp) => {
          this.health = resp || {};
          this.lastHealthAt = Date.now();
        })
        .finally(() => {
          this.healthLoading = false;
        });
    },
    loadCleanupPreview() {
      return apiSeoRankCleanupPreview({
        success_days: 90,
        failed_days: 90,
        raw_days: 30,
        command_success_days: 90,
      }).then((resp) => {
        this.cleanupPreview = resp || {};
      });
    },
    loadCycles(page) {
      if (page) this.cyclePager.page = page;
      return this.cyclePager
        .request({
          callback: apiSeoRankCycleList,
          params: { ...this.cycleParams },
        })
        .catch(() => {});
    },
    loadLegacyRuns(page) {
      if (page) this.legacyRunPager.page = page;
      return this.legacyRunPager
        .request({
          callback: apiSeoRankAutoRunList,
          params: { legacy: 1 },
        })
        .catch(() => {});
    },
    loadCommandRuns(page) {
      if (page) this.commandPager.page = page;
      return this.commandPager
        .request({
          callback: apiSeoRankCommandRunList,
          params: { ...this.commandParams },
        })
        .catch(() => {});
    },
    resetCycleFilters() {
      this.cycleParams = {
        cycle_type: "",
        status: "",
        period_key: "",
        keyword: "",
      };
      this.loadCycles(1);
    },
    reloadCycleDetail(cycleId) {
      const id = Number(cycleId || 0);
      if (!id) return Promise.resolve();
      const requestSeq = ++this.cycleDetailRequestSeq;
      this.cycleDetailTargetId = id;
      this.cycleDetailLoading = true;
      return Promise.all([
        apiSeoRankCycleDetail({ cycle_id: id }),
        apiSeoRankCycleTimeline({ cycle_id: id }),
      ])
        .then(([detail, timeline]) => {
          if (!this.isCurrentCycleDetailRequest(requestSeq, id)) return;
          const detailCycleId = Number(
            (detail && detail.cycle && detail.cycle.id) || 0
          );
          const timelineCycleId = Number(
            (timeline && timeline.cycle && timeline.cycle.id) || 0
          );
          if (detailCycleId !== id || timelineCycleId !== id) {
            const error = new Error("Cycle详情响应目标不一致，已拒绝更新页面");
            error.isTargetMismatch = true;
            throw error;
          }
          this.cycleDetail = detail || {};
          this.cycleTimeline = (timeline && timeline.events) || [];
        })
        .catch((error) => {
          if (!this.isCurrentCycleDetailRequest(requestSeq, id)) return;
          if (error && error.isTargetMismatch) {
            this.$message.error(error.message);
          }
        })
        .finally(() => {
          if (this.isCurrentCycleDetailRequest(requestSeq, id)) {
            this.cycleDetailLoading = false;
          }
        });
    },
    isCurrentCycleDetailRequest(requestSeq, cycleId) {
      return (
        Number(requestSeq) === Number(this.cycleDetailRequestSeq) &&
        Number(cycleId) === Number(this.cycleDetailTargetId) &&
        this.cycleDrawer.visible
      );
    },
    openCycleDetailId() {
      if (!this.cycleDrawer.visible) return 0;
      return Number(
        this.cycleDetailTargetId || (this.cycleDetail.cycle || {}).id || 0
      );
    },
    cycleDrawerHasLiveActivity() {
      if (!this.openCycleDetailId()) return false;
      const cycle = this.cycleDetail.cycle || {};
      if (!cycle.id || Number(cycle.id) !== this.openCycleDetailId()) {
        return true;
      }
      if (!terminalCycleStatuses.includes(cycle.status)) return true;
      return (this.cycleDetail.sites || []).some(
        (site) => site.status === "repairing"
      );
    },
    onCycleDrawerClose() {
      this.cycleDetailRequestSeq++;
      this.cycleDetailTargetId = 0;
      this.cycleDetailLoading = false;
      this.scheduleNextPoll();
    },
    refreshCycleViews(cycleId) {
      const requests = [
        this.loadCurrent(),
        this.loadCycles(),
        this.loadHealth(),
      ];
      if (
        this.openCycleDetailId() &&
        this.openCycleDetailId() === Number(cycleId || 0)
      ) {
        requests.push(this.reloadCycleDetail(cycleId));
      }
      return Promise.all(requests).catch(() => {});
    },
    openCycle(row) {
      this.cycleDrawer.visible = true;
      this.cycleDrawer.title = `Cycle详情 · ${row.cycle_no || row.id}`;
      this.cycleDetail = {};
      this.cycleTimeline = [];
      return this.reloadCycleDetail(row.id).finally(() => {
        this.scheduleNextPoll();
      });
    },
    openBatches(run) {
      this.selectedRun = run;
      this.batchDrawer.visible = true;
      this.batchDrawer.title = `Batch明细 · ${run.run_no || run.id}`;
      this.loadBatches(1);
    },
    loadBatches(page) {
      if (!this.selectedRun) return Promise.resolve();
      if (page) this.batchPager.page = page;
      return this.batchPager
        .request({
          callback: apiSeoRankAutoBatchList,
          params: { run_id: this.selectedRun.id },
        })
        .catch(() => {});
    },
    openTasks(batch) {
      this.selectedBatch = batch;
      this.taskDrawer.visible = true;
      this.taskDrawer.title = `Task明细 · ${batch.batch_no || batch.id}`;
      this.loadTasks(1);
    },
    loadTasks(page) {
      if (!this.selectedBatch) return Promise.resolve();
      if (page) this.taskPager.page = page;
      return this.taskPager
        .request({
          callback: apiSeoRankAutoTaskList,
          params: { batch_id: this.selectedBatch.id },
        })
        .catch(() => {});
    },
    manualCollect() {
      this.$confirm(
        "本操作只采集已存在的远端任务结果，不创建新的付费Task。是否继续？",
        "立即采集一轮",
        { type: "info" }
      )
        .then(() => {
          this.manualCollectLoading = true;
          return apiSeoRankManualCollect({ limit: 100, api_calls: 101 });
        })
        .then((resp) => {
          this.$message({
            type: "success",
            message: `采集完成：完成${resp.completed || 0}，排队${
              resp.queued || 0
            }，失败${resp.failed || 0}`,
          });
          this.refreshCycleViews((this.current.cycle || {}).id);
        })
        .catch(() => {})
        .finally(() => {
          this.manualCollectLoading = false;
        });
    },
    reconcileUnknown(cycle) {
      this.$confirm(
        "将通过 DataForSEO ID List 按签名Tag对账，只回填可确认的远端任务ID，不会重新POST。是否继续？",
        "提交未知对账",
        { type: "warning" }
      )
        .then(() => {
          this.reconcileLoading = true;
          return apiSeoRankReconcileUnknown({ cycle_id: cycle.id });
        })
        .then((resp) => {
          this.$message({
            type: resp.unmatched > 0 ? "warning" : "success",
            message: `对账完成：匹配${resp.matched || 0}，未匹配${
              resp.unmatched || 0
            }`,
          });
          this.refreshCycleViews(cycle.id);
        })
        .catch(() => {})
        .finally(() => {
          this.reconcileLoading = false;
        });
    },
    resumeCycle(cycle) {
      this.$confirm(
        "恢复操作本身不提交付费任务；下一轮调度器将按状态机继续。提交未知必须先完成对账。",
        "恢复Cycle",
        { type: "warning" }
      )
        .then(() => {
          this.resumeLoading = true;
          return apiSeoRankResumeCycle({ cycle_id: cycle.id });
        })
        .then((resp) => {
          this.$message({
            type: "success",
            message: `Cycle已恢复，当前状态：${this.statusText(resp.status)}`,
          });
          this.refreshCycleViews(cycle.id);
        })
        .catch(() => {})
        .finally(() => {
          this.resumeLoading = false;
        });
    },
    isRepairableCycle(cycle) {
      if (!cycle || !cycle.id) return false;
      return (
        ["completed", "partial_failed", "failed"].includes(cycle.status) &&
        Number(cycle.is_active || 0) !== 1 &&
        !cycle.active_guard
      );
    },
    unknownStateText(state) {
      const map = {
        none: "无提交未知",
        waiting_safety_window: "等待45分钟安全窗",
        awaiting_reconcile: "等待累计空对账",
        awaiting_post_wait_reconcile: "等待安全窗后的再次对账",
        eligible: "已满足确认条件",
      };
      return map[state] || state || "等待状态数据";
    },
    unknownResolutionTitle(resolution) {
      return `提交未知安全状态：${this.unknownStateText(
        (resolution || {}).state
      )}`;
    },
    unknownResolutionDescription(resolution) {
      const item = resolution || {};
      return `未知${Number(item.unknown_count || 0)}个；最少成功空对账${Number(
        item.min_reconcile_attempts || 0
      )}/${Number(
        item.required_reconcile_attempts || 3
      )}次；最早确认${this.timeText(item.eligible_at)}；最后对账${this.timeText(
        item.last_reconciled_at
      )}。确认未落地不会触发付费POST。`;
    },
    budgetText(value, nullable = false) {
      if (value === null || value === undefined || value === "") {
        return nullable ? "不适用" : "未设置上限";
      }
      if (!nullable && Number(value) <= 0) return "未设置上限";
      return this.formatUsd(value);
    },
    openAbandon(cycle, resolution = {}) {
      if (!this.isRootAdmin) return;
      this.abandonDialog = {
        visible: true,
        cycleId: Number(cycle.id || 0),
        cycleNo: cycle.cycle_no || "",
        resolution: { ...(resolution || {}) },
      };
      this.abandonForm = { cycle_no: "", reason: "" };
    },
    abandonUnknown() {
      if (!Number(this.abandonDialog.resolution.can_abandon || 0)) {
        this.$message.warning("尚未满足安全等待和空对账条件");
        return;
      }
      if (this.abandonForm.cycle_no !== this.abandonDialog.cycleNo) {
        this.$message.error("请输入完全一致的Cycle编号");
        return;
      }
      if (!this.abandonForm.reason) {
        this.$message.error("请填写确认原因");
        return;
      }
      this.abandonLoading = true;
      apiSeoRankAbandonUnknown({
        cycle_id: this.abandonDialog.cycleId,
        cycle_no: this.abandonForm.cycle_no,
        reason: this.abandonForm.reason,
      })
        .then((resp) => {
          this.$message({
            type: "success",
            message: `已确认${
              resp.abandoned_count || 0
            }个提交未知任务未落地；未触发付费POST`,
          });
          const cycleId = this.abandonDialog.cycleId;
          this.abandonDialog.visible = false;
          this.refreshCycleViews(cycleId);
        })
        .finally(() => {
          this.abandonLoading = false;
        });
    },
    openRepair(cycle, site = null) {
      if (!this.canResolve || !this.isRepairableCycle(cycle)) return;
      if (this.repairLoading) return;
      const siteId = Number((site && site.site_id) || 0);
      const cycleId = Number(cycle.id || 0);
      this.repairDialog = {
        visible: true,
        cycleId,
        cycleNo: cycle.cycle_no || "",
        siteId,
        siteName: siteId
          ? (site && (site.site_name || site.site_url)) || `站点ID ${siteId}`
          : "",
      };
      this.repairForm = { cycle_no: "", reason: "" };
      this.repairPreview = {};
      this.repairPreviewState = "loading";
      this.repairPreviewError = "";
      this.repairPreviewLoading = true;
      const requestSeq = ++this.repairPreviewRequestSeq;
      apiSeoRankRepairPreview({
        cycle_id: cycleId,
        site_id: siteId,
      })
        .then((resp) => {
          if (
            !this.isCurrentRepairPreviewRequest(requestSeq, cycleId, siteId)
          ) {
            return;
          }
          const preview = resp || {};
          if (
            Number(preview.cycle_id || 0) !== cycleId ||
            Number(preview.site_id || 0) !== siteId
          ) {
            const error = new Error("修复预览响应目标不一致，已禁止提交");
            error.isTargetMismatch = true;
            throw error;
          }
          this.repairPreview = preview;
          this.repairPreviewState =
            Number(preview.paid_resubmit_count || 0) > 0 ? "paid" : "free";
        })
        .catch((error) => {
          if (
            !this.isCurrentRepairPreviewRequest(requestSeq, cycleId, siteId)
          ) {
            return;
          }
          this.repairPreview = {};
          this.repairPreviewState = "failed";
          this.repairPreviewError = this.requestErrorText(
            error,
            "无法取得修复预览，请稍后重试"
          );
        })
        .finally(() => {
          if (this.isCurrentRepairPreviewRequest(requestSeq, cycleId, siteId)) {
            this.repairPreviewLoading = false;
          }
        });
    },
    isCurrentRepairPreviewRequest(requestSeq, cycleId, siteId) {
      return (
        Number(requestSeq) === Number(this.repairPreviewRequestSeq) &&
        this.repairDialog.visible &&
        Number(cycleId) === Number(this.repairDialog.cycleId || 0) &&
        Number(siteId) === Number(this.repairDialog.siteId || 0)
      );
    },
    onRepairDialogClose() {
      if (this.repairLoading) return;
      this.repairPreviewRequestSeq++;
      this.repairPreviewLoading = false;
      this.repairPreviewState = "idle";
      this.repairPreviewError = "";
      this.repairPreview = {};
    },
    requestErrorText(error, fallback) {
      if (typeof error === "string" && error) return error;
      if (error && error.message) return error.message;
      return fallback;
    },
    repairFailed() {
      if (!this.isRepairPreviewReady) {
        this.$message.error("修复预览尚未完成或目标已变化，请重新打开预览");
        return;
      }
      if (!Number(this.repairPreview.task_count || 0)) {
        this.$message.warning("当前范围没有可人工修复的失败任务");
        return;
      }
      if (this.repairForm.cycle_no !== this.repairDialog.cycleNo) {
        this.$message.error("请输入完全一致的Cycle编号");
        return;
      }
      if (!this.repairForm.reason) {
        this.$message.error("请填写修复原因");
        return;
      }
      const target = {
        cycleId: Number(this.repairDialog.cycleId || 0),
        cycleNo: this.repairDialog.cycleNo || "",
        siteId: Number(this.repairDialog.siteId || 0),
      };
      const expected = {
        taskCount: Number(this.repairPreview.task_count || 0),
        paidCount: Number(this.repairPreview.paid_resubmit_count || 0),
        freeCount: Number(this.repairPreview.free_collect_count || 0),
        estimatedCost: Number(this.repairPreview.estimated_cost || 0),
      };
      if (
        Number(this.repairPreview.cycle_id || 0) !== target.cycleId ||
        Number(this.repairPreview.site_id || 0) !== target.siteId ||
        (this.repairPreview.cycle_no &&
          this.repairPreview.cycle_no !== target.cycleNo)
      ) {
        this.$message.error("修复预览目标已变化，已禁止提交");
        return;
      }
      this.repairLoading = true;
      apiSeoRankRepairFailed({
        cycle_id: target.cycleId,
        site_id: target.siteId,
        cycle_no: this.repairForm.cycle_no,
        reason: this.repairForm.reason,
        expected_task_count: expected.taskCount,
        expected_paid_count: expected.paidCount,
        expected_free_count: expected.freeCount,
        expected_estimated_cost: expected.estimatedCost,
      })
        .then((resp) => {
          const unknown = Number(resp.submission_unknown || 0);
          const warning = unknown > 0 || Number(resp.blocked || 0) === 1;
          this.$message({
            type: warning ? "warning" : "success",
            duration: warning ? 8000 : 3000,
            message: unknown
              ? `有${unknown}个付费提交响应未知，请先执行ID List对账，禁止再次点击修复`
              : `修复已启动：免费采集${
                  resp.free_collect_queued || 0
                }，付费提交${resp.submitted || 0}，费用${this.formatUsd(
                  resp.cost
                )}`,
          });
          const cycleId = target.cycleId;
          this.repairDialog.visible = false;
          this.refreshCycleViews(cycleId);
        })
        .catch(() => {})
        .finally(() => {
          this.repairLoading = false;
        });
    },
    openForceClose(cycle) {
      this.forceCloseDialog = {
        visible: true,
        cycleId: Number(cycle.id),
        cycleNo: cycle.cycle_no || "",
      };
      this.forceCloseForm = { cycle_no: "", reason: "" };
    },
    forceCloseCycle() {
      if (this.forceCloseForm.cycle_no !== this.forceCloseDialog.cycleNo) {
        this.$message.error("请输入完全一致的Cycle编号");
        return;
      }
      if (!this.forceCloseForm.reason) {
        this.$message.error("请填写强制收口原因");
        return;
      }
      this.forceCloseLoading = true;
      apiSeoRankForceCloseCycle({
        cycle_id: this.forceCloseDialog.cycleId,
        cycle_no: this.forceCloseForm.cycle_no,
        reason: this.forceCloseForm.reason,
      })
        .then(() => {
          this.$message({ type: "success", message: "Cycle已强制收口" });
          this.forceCloseDialog.visible = false;
          this.refreshCycleViews(this.forceCloseDialog.cycleId);
        })
        .finally(() => {
          this.forceCloseLoading = false;
        });
    },
    startPolling() {
      this.pollingStopped = false;
      this.stopPolling();
      this.pollTick();
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    pollTick() {
      const requests = [this.loadCurrent(true).catch(() => {})];
      const detailCycleId = this.openCycleDetailId();
      if (detailCycleId) {
        requests.push(this.reloadCycleDetail(detailCycleId));
      }
      Promise.all(requests).finally(() => {
        if (this.pollingStopped) return;
        if (Date.now() - this.lastHealthAt >= 60000) {
          this.loadHealth().catch(() => {});
        }
        this.scheduleNextPoll();
      });
    },
    scheduleNextPoll() {
      if (this.pollingStopped) return;
      this.stopPolling();
      const active =
        (this.current.has_cycle &&
          !terminalCycleStatuses.includes((this.current.cycle || {}).status)) ||
        this.cycleDrawerHasLiveActivity();
      const delay = document.hidden ? 60000 : active ? 10000 : 60000;
      this.pollTimer = setTimeout(this.pollTick, delay);
    },
    onVisibilityChange() {
      if (this.pollingStopped) return;
      this.scheduleNextPoll();
    },
    statusText(status) {
      return statusMap[status] || status || "-";
    },
    statusTagType(status) {
      if (["completed", "published", "success"].includes(status)) {
        return "success";
      }
      if (
        [
          "blocked",
          "failed",
          "timeout",
          "expired",
          "force_closed",
          "submission_unknown",
        ].includes(status)
      ) {
        return "danger";
      }
      if (
        [
          "partial_failed",
          "published_partial",
          "retrying",
          "retry_pending",
        ].includes(status)
      ) {
        return "warning";
      }
      if (
        [
          "submitting",
          "submitted",
          "processing",
          "collecting",
          "running",
        ].includes(status)
      ) {
        return "primary";
      }
      return "info";
    },
    commandTagType(status) {
      if (status === "success") return "success";
      if (status === "failed") return "danger";
      if (status === "running") return "primary";
      return "info";
    },
    commandStatusLabel(status) {
      return statusMap[status] || status || "-";
    },
    commandStatusText(run) {
      if (!run || !run.id) return "暂无心跳";
      return this.commandStatusLabel(run.status);
    },
    timelineTagType(level) {
      if (level === "success") return "success";
      if (level === "warning") return "warning";
      if (level === "danger" || level === "error") return "danger";
      return "primary";
    },
    progressStatus(status) {
      if (status === "completed") return "success";
      if (["failed", "force_closed"].includes(status)) return "exception";
      if (status === "partial_failed") return "warning";
      return undefined;
    },
    cycleTypeText(type) {
      return type === "auto" ? "全站月度" : "手动单站";
    },
    runIndexesText(indexes) {
      return (indexes || []).map((item) => "R" + item).join("、") || "-";
    },
    timeText(value) {
      if (!value) return "-";
      return formatDate(Number(value), "yyyy-mm-dd hh:MM:ss");
    },
    formatUsd(value) {
      return `$${Number(value || 0).toFixed(6)} USD`;
    },
    durationText(value) {
      const ms = Number(value || 0);
      if (!ms) return "-";
      if (ms < 1000) return `${ms}ms`;
      return `${(ms / 1000).toFixed(1)}s`;
    },
    waitingDurationText(value) {
      const seconds = Math.max(0, Number(value || 0));
      if (!seconds) return "-";
      if (seconds >= 86400) return `${(seconds / 86400).toFixed(1)}天`;
      if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)}小时`;
      if (seconds >= 60) return `${Math.floor(seconds / 60)}分钟`;
      return `${Math.floor(seconds)}秒`;
    },
  },
};
</script>

<style lang="scss" scoped>
.seo-cycle-page {
  color: #303133;
}

.page-head,
.cycle-head,
.schedule-panel,
.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
}

.page-subtitle {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}

.head-actions,
.cycle-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cycle-detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.semantic-alert {
  margin: 18px 0 8px;
}

.schedule-panel {
  justify-content: flex-start;
  gap: 42px;
  padding: 18px 20px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fafafa;
}

.schedule-panel > div {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.schedule-panel span,
.metric-card span {
  color: #909399;
  font-size: 12px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.metric-grid.compact {
  margin-top: 20px;
}

.metric-card {
  min-height: 72px;
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
}

.metric-card strong {
  display: block;
  margin-top: 8px;
  font-size: 20px;
}

.section-block {
  margin-top: 24px;
}

.section-title {
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
}

.section-note {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
  font-weight: 400;
}

.section-gap {
  margin-top: 16px;
}

.sample-chip {
  display: inline-block;
  margin: 2px 6px 2px 0;
  padding: 2px 7px;
  border-radius: 10px;
  background: #f2f6fc;
  color: #606266;
  font-size: 12px;
}

.muted {
  color: #909399;
  font-size: 12px;
  line-height: 20px;
}

.execution-wrap {
  min-height: 280px;
}

.cycle-head {
  align-items: flex-start;
  margin-bottom: 18px;
}

.cycle-no {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
}

.cycle-meta {
  margin-top: 6px;
}

.progress-caption {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}

.status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.filter-form {
  margin-bottom: 4px;
}

.filter-select {
  width: 150px;
}

.filter-keyword {
  width: 220px;
}

.pagination-row {
  justify-content: flex-end;
  margin-top: 16px;
}

.legacy-block {
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}

.heartbeat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 12px;
}

.heartbeat-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafafa;
}

.heartbeat-card span,
.heartbeat-card small {
  color: #909399;
}

.command-select {
  width: 190px;
}

.ops-note {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}

.drawer-body {
  padding: 0 22px 30px;
}

.timeline-desc {
  margin-top: 5px;
}

.force-form {
  margin-top: 18px;
}

.dialog-metrics {
  margin-top: 18px;
}

@media (max-width: 1200px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }

  .schedule-panel {
    flex-wrap: wrap;
    gap: 22px 36px;
  }
}

@media (max-width: 720px) {
  .page-head,
  .cycle-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .metric-grid,
  .heartbeat-grid {
    grid-template-columns: 1fr;
  }
}
</style>
